import { LitElement, html } from 'lit';

import { navigate } from '../../../lib/router/index.js';
import { api } from '../../api/index.js';
import { customerLoadState } from '../../components/customer-load-state.js';
import { markdown } from '../../components/plane-markdown.js';
import { CUSTOMER_PERSONA, customerError, onlyAssistant, ownersOnly, workspaceName, workspacePath } from '../../i18n/customer-labels.js';
import { assistantSchema, getAt, parseList, setAt, validate } from '../../params-form.js';
import { orchestratorOf } from '../../team.js';
import { customerStyles } from '../../../styles/shared/customer.js';
import { formStyles } from '../../../styles/shared/forms.js';
import { markdownStyles } from '../../../styles/shared/markdown.js';
import { pageStyles } from '../../../styles/shared/page.js';

/** The fields a customer edits, where the params schema of the image version declares them. */
const FIELDS = {
  agentName: ['agentName'],
  avatar: ['avatar'],
  description: ['description'],
  allowFrom: ['telegram', 'allowFrom'],
  groups: ['telegram', 'groups'],
  acceptRequests: ['telegram', 'acceptRequests'],
  open: ['telegram', 'public'],
};

/** What to tell the customer about a field the schema refuses: the schema's words are for operators. */
const PROBLEMS = {
  agentName: 'Give it a name, up to 40 characters.',
  avatar: 'Choose another picture: a PNG, JPEG or WebP image.',
  description: 'Up to 500 characters.',
  'telegram.allowFrom': 'One Telegram user id per line: digits only.',
};

const declares = (schema, path) => getAt(schema, path.flatMap((key) => ['properties', key])) !== undefined;

/**
 * The editor of the assistant of a workspace, for its customer: its name, its personality, who can
 * write to it. Saving makes a new version of the workspace and applies it.
 *
 * Everything else in the parameters (the model, how much it thinks) is the operator's and is kept
 * as in the desired revision; the backend refuses a change of it anyway (x-audience). There are no
 * drafts: "Save" saves a new revision and applies it.
 *
 * It edits one assistant (`assistant`, its id; left out, the only one of the workspace): the fields
 * are those of the schema of one assistant, and the revision saved keeps every other assistant as
 * in the desired one.
 */
class AssistantEditorPage extends LitElement {
  static properties = {
    name: { type: String },
    assistant: { type: String },
    loaded: { state: true },
    params: { state: true },
    persona: { state: true },
    problems: { state: true },
    saving: { state: true },
    saveError: { state: true },
  };

  static styles = [pageStyles, formStyles, customerStyles, markdownStyles];

  constructor() {
    super();
    this.loader = { value: undefined, error: null, refresh: () => this.load() };
    this.problems = {};
    this.saving = false;
    this.saveError = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.load();
  }

  async load() {
    this.loader = { ...this.loader, value: undefined, error: null };
    this.requestUpdate();
    try {
      const instance = await api.instance(this.name);
      const [desired, image] = await Promise.all([
        instance.desiredRevision ? api.revision(this.name, instance.desiredRevision.id) : null,
        api.imageVersion(instance.imageVersion),
      ]);
      const id = this.assistant ?? onlyAssistant(desired);
      this.loaded = { instance, desired, schema: assistantSchema(image.paramsSchema), id };
      this.params = structuredClone(desired?.params.assistants?.[id] ?? {});
      this.persona = { soul: '', agents: '', user: '', ...structuredClone(desired?.personas?.[id] ?? {}) };
      this.loader = { ...this.loader, value: this.loaded };
    } catch (error) {
      this.loader = { ...this.loader, error };
    }
    this.requestUpdate();
  }

  set(path, value) {
    this.params = setAt(this.params, path, value);
  }

  /** The name of the assistant that delegates to this one, in a team; undefined outside a team. */
  orchestratorName() {
    const { desired, id } = this.loaded;
    const orchestrator = orchestratorOf(desired.params, id);
    return orchestrator && (desired.params.assistants[orchestrator]?.agentName || orchestrator);
  }

  async save() {
    const errors = validate(this.loaded.schema, this.params);
    this.problems = Object.fromEntries(
      Object.keys(errors).map((key) => {
        const field = Object.keys(PROBLEMS).find((prefix) => key === prefix || key.startsWith(`${prefix}.`));
        return [field ?? key, PROBLEMS[field] ?? 'Not valid.'];
      }),
    );
    if (this.orchestratorName() && !this.params.description?.trim()) {
      this.problems = { ...this.problems, description: `${this.orchestratorName()} reads it to choose this assistant: write a few words.` };
    }
    if (Object.keys(this.problems).length > 0) {
      this.saveError = 'Some fields are not right: see the messages in red.';
      return;
    }
    this.saving = true;
    this.saveError = null;
    try {
      const { desired, id } = this.loaded;
      const revision = await api.saveRevision(this.name, {
        // the other assistants as they are
        params: { ...desired.params, assistants: { ...desired.params.assistants, [id]: this.params } },
        personas: { ...desired.personas, [id]: this.persona },
      });
      await api.apply(this.name, revision.id);
      navigate(workspacePath(this.name));
    } catch (error) {
      this.saveError = customerError(error);
    } finally {
      this.saving = false;
    }
  }

  renderProblem(key) {
    return this.problems[key] ? html`<span class="problem">${this.problems[key]}</span>` : null;
  }

  render() {
    const back = workspacePath(this.name);
    const pending = customerLoadState(this.loader);
    if (pending) {
      return html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/">Your workspaces</a></nav>${pending}`;
    }
    const { instance, desired, schema, id } = this.loaded;
    const name = desired?.params.assistants?.[id]?.agentName ?? 'Your assistant';
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/">Your workspaces</a> / <a href=${back}>${workspaceName(instance)}</a></nav>`;
    if (!instance.canManage) {
      return html`${crumbs}<p class="empty">${ownersOnly(instance)}</p>`;
    }
    if (!desired) {
      return html`${crumbs}<p class="empty">${name} is being set up: you can change it once it is ready.</p>`;
    }
    if (!(id in desired.personas)) {
      // the address of the editor of the workspace when it has several assistants, or of an assistant that is gone
      return html`${crumbs}<p class="empty">Choose the assistant to change in <a href=${back}>its workspace</a>.</p>`;
    }
    const allowed = getAt(this.params, FIELDS.allowFrom) ?? [];
    return html`
      ${crumbs}
      <header class="page-head">
        <div>
          <h1>Edit ${name}</h1>
          <p class="lead">Your changes reach ${name} as soon as you save. The previous version stays in the history.</p>
        </div>
      </header>
      <form
        @submit=${(event) => {
          event.preventDefault();
          this.save();
        }}
      >
        ${declares(schema, FIELDS.agentName)
          ? html`
              <div class="field ${this.problems.agentName ? 'invalid' : ''}">
                <label for="agent-name">Name</label>
                <span class="help">The name it introduces itself with, for example “Anna” or “Bakery helper”. Up to 40 characters.</span>
                <input id="agent-name" type="text" maxlength="40" ?disabled=${this.saving} .value=${this.params.agentName ?? ''} @input=${(event) => this.set(FIELDS.agentName, event.target.value || undefined)} />
                ${this.renderProblem('agentName')}
              </div>
            `
          : null}
        ${declares(schema, FIELDS.avatar)
          ? html`
              <div class="field ${this.problems.avatar ? 'invalid' : ''}">
                <span class="label">Picture</span>
                <span class="help">Shown next to its name. Choose a photo or a logo from your computer or phone: it is cut to a square, so keep what matters in the middle.</span>
                <plane-avatar-input
                  .value=${this.params.avatar}
                  .label=${this.params.agentName}
                  .maxLength=${getAt(schema, ['properties', 'avatar', 'maxLength'])}
                  ?disabled=${this.saving}
                  @avatar-change=${(event) => this.set(FIELDS.avatar, event.detail.value)}
                ></plane-avatar-input>
                ${this.renderProblem('avatar')}
              </div>
            `
          : null}
        ${desired.operatorRules?.trim()
          ? html`
              <div class="field">
                <span class="label">Rules from your provider</span>
                <span class="help">Your assistant always follows them, before your own rules. Only your provider changes them.</span>
                <div class="markdown">${markdown(desired.operatorRules)}</div>
              </div>
            `
          : null}
        ${CUSTOMER_PERSONA.map(
          (part) => html`
            <div class="field">
              <label for="persona-${part.key}">${part.title}</label>
              <span class="help">${part.description}</span>
              <plane-markdown-input
                input-id="persona-${part.key}"
                input-class="prose"
                rows="7"
                placeholder=${part.example}
                ?disabled=${this.saving}
                .value=${this.persona[part.key]}
                @input=${(event) => {
                  this.persona = { ...this.persona, [part.key]: event.target.value };
                }}
              ></plane-markdown-input>
            </div>
          `,
        )}
        ${declares(schema, FIELDS.description) && this.orchestratorName()
          ? html`
              <div class="field ${this.problems.description ? 'invalid' : ''}">
                <label for="description">What it is for</label>
                <span class="help">
                  People do not write to it: ${this.orchestratorName()} asks it when a question needs it, and reads this to choose. Say what it knows about, and what not.
                </span>
                <textarea
                  id="description"
                  class="prose"
                  rows="3"
                  maxlength="500"
                  ?disabled=${this.saving}
                  .value=${this.params.description ?? ''}
                  @input=${(event) => this.set(FIELDS.description, event.target.value || undefined)}
                ></textarea>
                ${this.renderProblem('description')}
              </div>
            `
          : null}
        ${declares(schema, FIELDS.allowFrom) && !this.orchestratorName()
          ? html`
              <div class="field ${this.problems['telegram.allowFrom'] ? 'invalid' : ''}">
                <label for="allow-from">Who can write to it on Telegram</label>
                <span class="help">
                  Only these people get an answer; anybody else is ignored. Write their Telegram user id, one per line: a number like 123456789, not a phone number. To find
                  it, each person writes to <strong>@userinfobot</strong> on Telegram, which answers with their id. Put your own id first: the first one is the assistant's owner.
                </span>
                <textarea id="allow-from" class="prose" rows="3" placeholder=${'123456789\n987654321'} ?disabled=${this.saving} .value=${allowed.join('\n')} @change=${(event) => this.set(FIELDS.allowFrom, parseList(event.target.value))}></textarea>
                ${this.renderProblem('telegram.allowFrom')}
              </div>
            `
          : null}
        ${declares(schema, FIELDS.acceptRequests) && getAt(this.params, FIELDS.open) !== true && !this.orchestratorName()
          ? html`
              <div class="field">
                <label class="checkbox">
                  <input
                    type="checkbox"
                    ?disabled=${this.saving}
                    .checked=${getAt(this.params, FIELDS.acceptRequests) === true}
                    @change=${(event) => this.set(FIELDS.acceptRequests, event.target.checked)}
                  />
                  Accept requests from new people
                </label>
                <span class="help">
                  Whoever writes to the bot and is not on the list gets a code and waits: you see them in the workspace, with that code, and allow them with one click. No
                  user id to look up. Off: the bot ignores whoever is not on the list.
                </span>
              </div>
            `
          : null}
        ${getAt(this.params, FIELDS.open) === true
          ? html`<p class="muted">The bot is open to everyone, by choice of your provider: anyone who finds it can talk to it. The people on the list are the only ones
              who can use its commands.</p>`
          : null}
        ${declares(schema, FIELDS.groups) && !this.orchestratorName()
          ? html`
              <div class="field">
                <label class="checkbox">
                  <input type="checkbox" ?disabled=${this.saving} .checked=${getAt(this.params, FIELDS.groups) === true} @change=${(event) => this.set(FIELDS.groups, event.target.checked)} />
                  Answer in group chats when mentioned
                </label>
                <span class="help">Add it to a Telegram group and it answers only when somebody writes its name. Off: it talks only in private chats.</span>
              </div>
            `
          : null}
        <div class="buttons">
          <button class="primary" type="submit" ?disabled=${this.saving}>${this.saving ? 'Saving…' : 'Save'}</button>
          <a class="button" href=${back}>Cancel</a>
          ${this.saveError ? html`<span class="form-error" role="alert">${this.saveError}</span>` : null}
        </div>
      </form>
    `;
  }
}

customElements.define('plane-assistant-editor-page', AssistantEditorPage);
